"use client";

import React from "react";
import { motion } from "framer-motion";
import { Droplets, Gauge, Wind, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const keyPoints = [{
  id: 1,
  title: "Controlo rigoroso da humidade relativa",
  description: "Sistemas de atomização a ar e hidráulica que mantêm os níveis de humidade precisos sem molhar superfícies ou equipamentos.",
  metric: "±2%",
  metricLabel: "Precisão higrométrica"
}, {
  id: 2,
  title: "Prevenção de eletricidade estática",
  description: "Elimine paragens produtivas em linhas de impressão, têxteis, papel e salas limpas industriais.",
  metric: "0",
  metricLabel: "Falhas por ESD"
}, {
  id: 3,
  title: "Redução de poeiras e controlo térmico",
  description: "Gotículas microscópicas evaporam instantaneamente, arrefecendo o ambiente e precipitando partículas em suspensão.",
  metric: "100%",
  metricLabel: "Evaporação imediata"
}];
const technicalHighlights = ["Bicos de atomização fina Spraying Systems Co.", "Sistemas de controlo AutoJet integrados", "Sem condensação em chão ou maquinaria", "Baixo consumo de energia e água"];
export default function Humidificacao() {
  return <section id="herohumidificacao" data-nav="dark" className="relative min-h-[85vh] overflow-hidden bg-primary text-primary-foreground flex items-center justify-center py-20 lg:py-28">
      {/* Subtle Background Glows and Atmospheric Water Droplets Grid */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 size-96 rounded-full bg-secondary/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 size-96 rounded-full bg-secondary/15 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-center">
          
          {/* Main Hero Copy */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="bg-secondary/30 text-primary-foreground border-secondary/40 px-3 py-1 text-xs sm:text-sm font-medium backdrop-blur-sm">
                <Droplets className="size-3.5 mr-1.5 text-secondary-foreground" />
                Sistemas de Engenharia & Pulverização
              </Badge>
              <span className="text-xs text-primary-foreground/70 hidden sm:inline-block">
                Representante Spraying Systems Co.
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1,
            ease: "easeOut"
          }} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-primary-foreground font-default leading-[1.1]">
              Humidificação industrial e controlo de humidade
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.2,
            ease: "easeOut"
          }} className="mt-6 text-lg sm:text-xl text-primary-foreground/85 max-w-2xl font-normal leading-relaxed">
              Garantia de estabilidade higrométrica e prevenção de cargas estáticas com sistemas automatizados de atomização de elevada precisão.
            </motion.p>

            {/* Quick Benefits Bullet List */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.3,
            ease: "easeOut"
          }} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
              {technicalHighlights.map((point, index) => <div key={point} data-index={index} className="flex items-center gap-2 text-sm text-primary-foreground/90">
                  <CheckCircle2 className="size-4 shrink-0 text-secondary-foreground" />
                  <span>{point}</span>
                </div>)}
            </motion.div>

            {/* Actions */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.4,
            ease: "easeOut"
          }} className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md font-medium px-6 py-6 text-base">
                <Link to="/ContactsPage">
                  Consulte-nos
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/20 text-primary-foreground bg-primary/40 hover:bg-primary-foreground/10 hover:text-primary-foreground px-6 py-6 text-base">
                <Link to="/ProductsPage">
                  Explorar bicos atomizadores
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Feature Specs Visual Card */}
          <div className="lg:col-span-5 w-full">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25,
            ease: "easeOut"
          }} className="relative rounded-2xl border border-primary-foreground/15 bg-primary-foreground/5 backdrop-blur-md p-6 sm:p-8 shadow-xl">
              <div className="flex items-center justify-between border-b border-primary-foreground/10 pb-4 mb-6">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-secondary/30 text-secondary-foreground">
                    <Gauge className="size-5" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-primary-foreground">
                      Especificações de aplicação
                    </h2>
                    <p className="text-xs text-primary-foreground/70">
                      Gama de humidificação Eurocontrol
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="border-secondary/50 text-secondary-foreground bg-secondary/10 text-[11px]">
                  Indústria 4.0
                </Badge>
              </div>

              <div className="space-y-4">
                {keyPoints.map((item, idx) => <div key={item.id} data-index={idx} className="p-4 rounded-xl bg-primary/40 border border-primary-foreground/10 hover:border-secondary/40 transition-[border-color,background-color] duration-200">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-primary-foreground font-default">
                          {item.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-primary-foreground/80 mt-1 leading-normal">
                          {item.description}
                        </p>
                      </div>
                      <div className="text-right shrink-0 bg-secondary/20 border border-secondary/30 rounded-lg px-2.5 py-1.5 min-w-[70px]">
                        <span className="block text-base font-bold text-secondary-foreground font-mono">
                          {item.metric}
                        </span>
                        <span className="block text-[10px] text-primary-foreground/70 leading-none">
                          {item.metricLabel}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 pt-4 border-t border-primary-foreground/10 flex items-center justify-between text-xs text-primary-foreground/70">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="size-4 text-secondary-foreground" />
                  <span>Conformidade técnica CE & ISO</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Wind className="size-4 text-secondary-foreground" />
                  <span>Micro-gotículas &lt; 10 µm</span>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}